import { useTenderActionError } from "../../../../../hooks/useTenderActionError";
import React, { useEffect, useState } from "react";
import ReactDOM from "react-dom";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import style from "./UpdateComplexBidModal.module.scss";
import { Expeditor } from "../../../../../services";
import CustomInput from "../../../../custom/custom-input/CustomInput";
import CustomInputCurrency from "../../../../custom/custom-input-currency/CustomInputCurrency";
import CustomButton from "../../../../custom/custom-button/CustomButton";
import CloseIconNew from "../../../../shared/icons/CloseIconNew";
import HintIcon from "../../../../shared/icons/HintIcon";
import { CURRENCIES } from "../../../../../mock";
import { isRailwayOrder } from "../../../../../pages/orders/utils/railwayOrder";

const UpdateComplexBidModal = ({ onRefreshOrder, isOpen, onClose, bid, order, onSuccess }) => {
  const { t } = useTranslation();
  const transportContext = isRailwayOrder(order) ? "railway" : undefined;
  const { requestErrorDisplay } = useTenderActionError(order, { onRefreshOrder });
  const [price, setPrice] = useState(bid?.price ?? "");
  const [currency, setCurrency] = useState(bid?.currency || "KZT");
  const [vehiclesCount, setVehiclesCount] = useState("");
  const [errorText, setErrorText] = useState("");
  const [vehiclesErrorText, setVehiclesErrorText] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const lockCurrency = Boolean(order?.acceptedCurrency);
  const maxVehiclesCount = Number(order?.vehiclesCount) || 0;
  const isAuction = order?.assignmentMethod === "Auction";
  const reductionStep = Number(order?.bidReductionStep);
  const hasReductionStep = isAuction && Number.isFinite(reductionStep) && reductionStep > 0;
  const currentBestPrice = Number(bid?.minPrice ?? bid?.bestPrice);
  const hasCurrentBestPrice =
    (bid?.minPrice != null || bid?.bestPrice != null) &&
    Number.isFinite(currentBestPrice) &&
    currentBestPrice > 0;
  const currencySymbol = currency === "KZT" ? "₸" : CURRENCIES[currency]?.symbol || currency;
  const stepPrice = hasReductionStep ? `${reductionStep.toLocaleString("ru-RU")} ${currencySymbol}` : "";

  useEffect(() => {
    if (!isOpen) return;
    setPrice(bid?.price ?? "");
    setCurrency(bid?.currency || order?.acceptedCurrency || "KZT");
    setVehiclesCount(bid?.vehiclesCount ?? "");
    setErrorText("");
    setVehiclesErrorText("");
  }, [isOpen, bid, order?.acceptedCurrency]);

  if (!isOpen) return null;

  const handleClose = () => {
    setPrice("");
    setCurrency(bid?.currency || "KZT");
    setVehiclesCount("");
    setErrorText("");
    setVehiclesErrorText("");
    onClose();
  };

  const handleUpdate = async () => {
    const rounded = Math.round(Number(price));
    const isPriceChanged = rounded !== Number(bid?.price);
    const nextVehiclesCount = Number(vehiclesCount);

    if (!rounded || rounded <= 0) {
      setErrorText(t("complexTender.updateBid.newBidRequired"));
      return;
    }

    if (rounded > 2000000000) {
      setErrorText(t("complexTender.updateBid.bidTooLarge"));
      return;
    }

    if (isPriceChanged && isAuction && hasCurrentBestPrice && rounded >= currentBestPrice) {
      setErrorText(t("complexTender.updateBid.belowBestBidRequired"));
      return;
    }

    if (isPriceChanged && hasReductionStep && hasCurrentBestPrice && rounded > currentBestPrice - reductionStep) {
      setErrorText(t("complexTender.updateBid.reductionStepError", { step: stepPrice }));
      return;
    }

    if (!nextVehiclesCount || nextVehiclesCount <= 0) {
      setVehiclesErrorText(t("complexTender.updateBid.vehiclesRequired", { context: transportContext }));
      return;
    }

    if (maxVehiclesCount && nextVehiclesCount > maxVehiclesCount) {
      setVehiclesErrorText(t("complexTender.updateBid.maxVehicles", {
        count: maxVehiclesCount,
        context: transportContext,
      }));
      return;
    }

    setIsLoading(true);
    try {
      await Expeditor.updateComplexOrderBid(bid.id, {
        price: rounded,
        currency,
        vehiclesCount: nextVehiclesCount,
      });
      toast.success(t("complexTender.updateBid.bidUpdated"));
      if (onSuccess) onSuccess();
    } catch (error) {
      await requestErrorDisplay(error, t, "complexTender.updateBid.updateBidError");
    } finally {
      setIsLoading(false);
    }
  };

  return ReactDOM.createPortal(
    <div className={style.overlay} onClick={handleClose} data-portal-modal="true">
      <div className={style.modal} onClick={(e) => e.stopPropagation()}>
        <div className={style.header}>
          <span className={style.title}>{t("complexTender.updateBid.title")}</span>
          <button className={style.close_btn} onClick={handleClose} aria-label={t("common.close")}>
            <CloseIconNew width="24" height="24" fill="#212121" />
          </button>
        </div>
        <div className={style.body}>
          <CustomInputCurrency
            value={price}
            isBgGray
            inputType="number"
            labelText={t("complexTender.updateBid.newBidLabel")}
            placeholder={t("complexTender.updateBid.yourBidPlaceholder")}
            errorText={errorText}
            currency={currency}
            lockCurrency={lockCurrency}
            onChangeValue={(val) => {
              setPrice(val);
              setErrorText("");
            }}
            onChangeCurrency={setCurrency}
          />
          {hasReductionStep && (
            <div className={style.reduction_step_hint}>
              <HintIcon aria-hidden="true" />
              <span>{t("complexTender.updateBid.reductionStepHint", { step: stepPrice })}</span>
            </div>
          )}
          <CustomInput
            value={vehiclesCount}
            isBgGray
            inputType="number"
            labelText={t("complexTender.updateBid.vehiclesCount", { context: transportContext })}
            placeholder={t("complexTender.updateBid.vehiclesCount", { context: transportContext })}
            errorText={vehiclesErrorText}
            onChangeValue={(value) => {
              const nextValue = Number(value) || "";
              setVehiclesCount(maxVehiclesCount && nextValue ? Math.min(maxVehiclesCount, nextValue) : nextValue);
              setVehiclesErrorText("");
            }}
          />
        </div>

        <div className={style.footer}>
          <div className={style.footer_btn}>
            <CustomButton
              text={t("common.cancel")}
              isNavy
              isWhite
              onClick={handleClose}
              disabled={isLoading}
              style={{ width: "100%" }}
            />
          </div>
          <div className={style.footer_btn}>
            <CustomButton
              text={t("complexTender.updateBid.update")}
              isNavy
              onClick={handleUpdate}
              isLoading={isLoading}
              disabled={isLoading}
              style={{ width: "100%" }}
            />
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default UpdateComplexBidModal;
